import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import restaurantImg from "../assets/restorent/restaurant.png";
import { pudukkottaiPageSchema } from "../data/schemas";

const FAQS = [
  {
    q: "Which is the best restaurant in Pudukkottai for family dinners and biryani?",
    a: "For wood-fire dum biryani, double marinated grill chicken and a relaxed family setting, Barbequeen Restaurant is a dependable choice. Our recipes have been served across Tamil Nadu since 1999.",
  },
  {
    q: "Is Barbequeen the best family restaurant in Pudukkottai?",
    a: "We'll let you be the judge. Our menu runs from mild to spicy, and bucket biryani makes sharing easy for big families.",
  },
  {
    q: "What makes Barbequeen stand out as a biryani restaurant in Pudukkottai?",
    a: "We still cook on wood fire, dum style, with seeraga samba rice. Every plate comes with khatta gravy and onion raitha.",
  },
  {
    q: "What should I order on my first visit?",
    a: "Share a chicken or mutton biryani and a grill chicken. If anyone at the table prefers milder food, add a mandi.",
  },
  {
    q: "Is the menu in Pudukkottai the same as at other Barbequeen outlets?",
    a: "Our core dishes, including biryani, grill chicken and tandoori, follow the same recipes everywhere. A few items can vary by outlet, so ask our team what's on today.",
  },
];

const OTHER_GUIDES = [
  {
    title: "Best Restaurant in Coimbatore",
    path: "/best-restaurant-in-coimbatore",
    city: "Coimbatore",
  },
  {
    title: "Best Restaurant in Salem",
    path: "/best-restaurant-in-salem",
    city: "Salem",
  },
  {
    title: "Best Restaurant in Erode",
    path: "/best-restaurant-in-erode",
    city: "Erode",
  },
  {
    title: "Best Restaurant in Krishnagiri",
    path: "/best-restaurant-in-krishnagiri",
    city: "Krishnagiri",
  },
];

const BestRestaurantInPudukkottaiPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (idx) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  const pageTitle =
    "Best Restaurant in Pudukkottai: Why Families Choose Barbequeen";
  const pageUrl = "https://barbequeen.com/blog/best-restaurant-in-pudukkottai";

  return (
    <div
      className="min-h-screen bg-[#f9f5f0]"
      style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif" }}
    >
      {/* ── SEO & Schemas ── */}
      <SEO
        title={pageTitle}
        metaTitle={pageTitle}
        description="Looking for the best restaurant in Pudukkottai? Enjoy authentic wood-fire seeraga samba biryani, double-marinated grills, and family-friendly dining at Barbequeen."
        keywords="Best restaurant in Pudukkottai, best family restaurant in Pudukkottai, biryani restaurant in Pudukkottai, Barbequeen Pudukkottai, wood fire biryani, seeraga samba biryani, grill chicken Pudukkottai"
        robots="index, follow"
        canonical={pageUrl}
        geo={{
          region: "IN-TN",
          placename: "Pudukkottai, Tamil Nadu",
          position: "10.3833;78.8001",
          icbm: "10.3833, 78.8001",
        }}
        og={{
          type: "article",
          url: pageUrl,
          title: "Best Restaurant in Pudukkottai: Why Families Choose Barbequeen",
          description:
            "Discover wood-fire dum biryani, double-marinated grill chicken, and family-friendly dining at Barbequeen Pudukkottai. Served since 1999.",
          image: "https://barbequeen.com/images/barbequeen-pudukkottai-og.jpg",
          imageWidth: 1200,
          imageHeight: 630,
          siteName: "Barbequeen Restaurant",
          locale: "en_IN",
        }}
        schema={pudukkottaiPageSchema}
      />

      {/* ── Hero Banner ── */}
      <section
        className="relative overflow-hidden"
        style={{ minHeight: "360px" }}
      >
        <img
          src="/images/barbequeen-pudukkottai-og.jpg"
          alt="Best Restaurant in Pudukkottai - Barbequeen Restaurant"
          className="absolute inset-0 w-full h-full object-cover object-center scale-105"
          onError={(e) => {
            e.target.src = restaurantImg;
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(10,0,2,0.82) 0%, rgba(10,0,2,0.92) 100%)",
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 pt-28 sm:pt-32 pb-14 text-left">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/60 mb-5">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <svg
              className="w-3 h-3 text-white/40"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
            <Link to="/blog" className="hover:text-white transition-colors">
              Blog
            </Link>
            <svg
              className="w-3 h-3 text-white/40"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
            <span className="text-white/90 font-medium truncate max-w-[200px] sm:max-w-xs">
              Pudukkottai Guide
            </span>
          </nav>

          {/* Category Tag */}
          <span className="inline-block px-3 py-1 rounded-full text-white text-[10px] font-bold tracking-[0.2em] uppercase mb-4 bg-[#9b1c2e]">
            Restaurant Guide • Pudukkottai
          </span>

          {/* Title */}
          <h1 className="text-2xl sm:text-4xl lg:text-[40px] font-bold text-white leading-snug tracking-wide uppercase max-w-4xl mb-4">
            Best Restaurant in Pudukkottai: Why Families Choose Barbequeen
          </h1>

          {/* Meta Bar */}
          <div className="flex flex-wrap items-center gap-5 text-xs text-white/80 font-sans pt-2">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#9b1c2e] flex items-center justify-center text-white font-bold text-xs">
                B
              </div>
              <span className="font-semibold text-white">
                Barbequeen Editorial
              </span>
            </div>
            <span className="text-white/30">•</span>
            <div className="flex items-center gap-1.5 text-white/70">
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>5 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Author / Social Share Row ── */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 py-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#9b1c2e] flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">
              B
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                Barbequeen Editorial
              </p>
              <p className="text-xs text-gray-400">
                Serving Pudukkottai & Tamil Nadu Since 1999
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="px-3 py-1 rounded-full border border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wide bg-white">
              Pudukkottai City Guide
            </span>
            <span className="text-sm text-gray-500">
              SIPCOT Industrial Park
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400 font-medium">Share:</span>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(pageTitle + " " + pageUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-[#25d366] flex items-center justify-center text-white hover:opacity-90 transition-opacity"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white hover:opacity-90 transition-opacity"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href={`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-[#1877f2] flex items-center justify-center text-white hover:opacity-90 transition-opacity"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* ── Main Content + Sidebar Layout ── */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* ── Left Column: Article Body ── */}
          <main className="lg:col-span-8">
            {/* Excerpt */}
            <p className="text-[16px] text-gray-700 leading-[1.85] mb-8 italic border-l-4 border-[#9b1c2e] pl-4 bg-white p-4 rounded-r-lg shadow-sm">
              Ask ten people in Pudukkottai where to eat and you'll get ten
              answers, usually followed by an argument about whose biryani is
              better. We like that. It means this town takes its food seriously,
              and so do we.
            </p>

            <p className="text-[15px] text-gray-600 leading-[1.85] mb-6">
              Barbequeen Restaurant has been grilling chicken and slow-cooking
              biryani in Tamil Nadu since 1999. Our Pudukkottai outlet brings
              those same recipes and the same wood fire to your neighbourhood.
              If you're trying to find the{" "}
              <strong>best restaurant in Pudukkottai</strong>, this guide
              explains what we think “best” should mean, so you can decide
              whether we've earned it.
            </p>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-8 mb-4 border-b border-gray-200 pb-2">
              What Makes the Best Restaurant in Pudukkottai?
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              After more than 25 years of cooking for families across Tamil
              Nadu, we've learned that diners judge a restaurant on four things.
            </p>
            <div className="space-y-3 mb-6">
              {[
                {
                  title: "Consistency",
                  text: "The biryani you loved last month should taste exactly the same today.",
                },
                {
                  title: "Fair prices",
                  text: "A clear menu, honest portions and no surprises on the bill.",
                },
                {
                  title: "Comfort for all ages",
                  text: "Children, parents and grandparents should all feel at ease at one table.",
                },
                {
                  title: "Service that listens",
                  text: "Staff who tell you how spicy a dish really is before you order it.",
                },
              ].map((item, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <span className="w-2 h-2 rounded-full bg-[#9b1c2e] mt-2 flex-shrink-0" />
                  <p className="text-[15px] text-gray-700 leading-[1.8] flex-1">
                    <strong>{item.title}.</strong> {item.text}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-6">
              Pudukkottai diners aren't easy to impress. Part of the Chettinad
              belt falls inside this district, so people here know a well-made
              masala the moment they taste it. That's the standard we hold our
              kitchen to.
            </p>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-10 mb-4 border-b border-gray-200 pb-2">
              Since 1999: The Kitchen Behind Barbequeen
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              Barbequeen began serving Salem in 1999 and now has outlets across
              Tamil Nadu, including Erode, Coimbatore and Thanjavur. Our
              Pudukkottai restaurant cooks from the same recipe book.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              Take our signature grill chicken. It's double marinated: first
              with a seasoning that soaks deep into the meat, then with a spice
              coating that chars over the flame. Skip either step and you'd
              taste the difference straight away.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-6">
              That shared method is the point. A guest who has eaten with us in
              Salem or Thanjavur should find the same flavour on their plate in
              Pudukkottai.
            </p>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-10 mb-4 border-b border-gray-200 pb-2">
              Best Restaurant in Pudukkottai for Family Get-Togethers
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              A family dinner here rarely means two people. It's grandparents,
              cousins and a couple of hungry kids, and our menu is planned with
              that table in mind. It runs from mild to fiery, so the aunt who
              avoids chilli and the cousin who wants extra spice can order from
              the same card.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              Sharing is part of the meal. Our bucket biryani goes in the
              middle of the table, everyone serves themselves, and nobody sits
              waiting for their own plate.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-6">
              If you're looking for the{" "}
              <strong>best family restaurant in Pudukkottai</strong>, check for
              three things: a menu with range, portions that suit a group and
              staff who stay patient with children. We work hardest on exactly
              those.
            </p>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-10 mb-4 border-b border-gray-200 pb-2">
              Biryani Restaurant in Pudukkottai: Wood Fire and Seeraga Samba
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              Biryani is the dish we're most stubborn about. Running a{" "}
              <Link
                to="/blog/best-biryani-in-pudukkottai"
                className="text-[#9b1c2e] font-semibold underline underline-offset-2 hover:text-[#7d1624]"
              >
                biryani restaurant in Pudukkottai
              </Link>{" "}
              on gas burners would be quicker, but we still cook ours on wood
              fire, in the dum style linked to the Nawabs of Arcot.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              The rice is seeraga samba, the small, fragrant grain loved by
              Tamil Nadu biryani cooks. It gets its name from its likeness to
              seeragam, or cumin seeds. The grains soak up the masala but stay
              separate when cooked right.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              Once the rice goes into the meat masala, the pot is sealed and
              left on a low wood flame to finish in its own steam. The slow,
              even heat keeps the meat tender and the rice light.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-6">
              When a fresh pot is opened, the smell of mint and whole spices
              fills the room before anyone sees the rice. Each plate comes with
              tangy khatta gravy and onion raitha. Choose from chicken, mutton,
              egg or chicken 65 biryani.
            </p>

            {/* Section 5 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-10 mb-4 border-b border-gray-200 pb-2">
              Beyond Biryani: Grills, Tandoori and Mandi
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              Biryani may get the headlines, but the grill is where the
              Barbequeen name comes from. Alongside the grill chicken you'll
              find tandoori chicken marinated in yoghurt, lemon and spices,
              plus chicken tikka and seekh kebabs.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              Want something gentler on spice? Try our mandi, a dish from
              Yemen where meat is slow-cooked and served over fragrant,
              lightly seasoned rice.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-6">
              Grills pair well with kuboos, parotta or butter naan. And if the
              kids only want fried rice or noodles, our Chinese section has
              them covered.
            </p>

            {/* Section 6 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-10 mb-4 border-b border-gray-200 pb-2">
              Clean Kitchen, Clear Prices, Honest Answers
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              Trust is earned at the table, not in a blog post, so we'll keep
              this simple. Our FSSAI licence is displayed at the outlet, and
              our kitchen follows the hygiene standards that come with it.
              Prices are printed clearly on the menu, and taxes are shown
              separally on the bill.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              Ask our staff how spicy a dish is, what goes into a marinade or
              how long the grill will take, and you'll get a straight answer. If
              someone at your table has a food allergy, tell us before you
              order.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-6">
              Don't just take our word for any of this. Read our Google
              reviews, then come and see for yourself.
            </p>

            {/* Section 7: Outlet Details */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-10 mb-4 border-b border-gray-200 pb-2">
              Visit Barbequeen in Pudukkottai
            </h2>
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:border-[#9b1c2e]/40 hover:shadow-md transition-all duration-200 mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Barbequeen Restaurant - SIPCOT Industrial Park
              </h3>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                Plot No. 3 B/3, Door No. 1665, Nalan Food Products, SIPCOT
                Industrial Park, Pudukkottai, Tamil Nadu 622002
              </p>
              <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-gray-100 text-sm">
                <a
                  href="tel:+918675795788"
                  className="font-bold text-[#9b1c2e] hover:underline flex items-center gap-1.5"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  +91 86757 95788
                </a>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Barbequeen+Restaurant+SIPCOT+Industrial+Park+Pudukkottai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-[#9b1c2e] font-semibold"
                >
                  View on Google Maps →
                </a>
              </div>
            </div>

            {/* Section 7: FAQs */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-12 mb-6 border-b border-gray-200 pb-2">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3 mb-10">
              {FAQS.map((faq, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-all duration-200"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 font-semibold text-gray-800 text-sm hover:text-[#9b1c2e] transition-colors"
                  >
                    <span>{faq.q}</span>
                    <span className="text-[#9b1c2e] text-lg font-bold flex-shrink-0">
                      {openFaq === idx ? "−" : "+"}
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4 text-xs sm:text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Tags / Bottom Share */}
            <div className="mt-10 pt-6 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="font-semibold text-gray-700">Tags:</span>
                <span className="bg-gray-100 px-2.5 py-1 rounded-md">
                  Best Restaurant in Pudukkottai
                </span>
                <span className="bg-gray-100 px-2.5 py-1 rounded-md">
                  Pudukkottai Biryani
                </span>
                <span className="bg-gray-100 px-2.5 py-1 rounded-md">
                  SIPCOT BBQ
                </span>
              </div>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(pageTitle + " " + pageUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#25d366] text-white text-xs font-bold rounded-lg hover:opacity-90 transition-opacity"
              >
                Share on WhatsApp
              </a>
            </div>
          </main>

          {/* ── Right Column: Sidebar ── */}
          <aside className="lg:col-span-4 space-y-6">
            {/* Other Cities Widget */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#9b1c2e]"></span>
                Other City Guides
              </h3>
              <div className="space-y-3">
                {OTHER_GUIDES.map((guide, idx) => (
                  <Link
                    key={idx}
                    to={guide.path}
                    className="group block p-3 rounded-xl bg-gray-50 hover:bg-[#9b1c2e]/5 border border-transparent hover:border-[#9b1c2e]/20 transition-all duration-200"
                  >
                    <span className="text-xs font-bold uppercase tracking-wider text-[#9b1c2e] block mb-0.5">
                      {guide.city}
                    </span>
                    <p className="text-sm font-semibold text-gray-800 group-hover:text-[#9b1c2e] transition-colors">
                      {guide.title} →
                    </p>
                  </Link>
                ))}
              </div>
            </div>

            {/* Quick Actions Widget */}
            <div className="bg-[#1a0a0d] text-white rounded-2xl p-6 shadow-md">
              <span className="text-[10px] font-bold tracking-[0.25em] text-[#c0424e] uppercase block mb-2">
                SINCE 1999
              </span>
              <h3 className="text-lg font-bold mb-3">
                Visit Barbequeen Pudukkottai
              </h3>
              <p className="text-xs text-gray-300 leading-relaxed mb-5">
                Taste the legend at SIPCOT Industrial Park with our signature
                double-marinated charcoal grill, wood-fire dum biryani, or
                Arabian chicken mandi.
              </p>
              <div className="space-y-2.5">
                <Link
                  to="/menu"
                  className="block w-full py-2.5 text-center bg-[#9b1c2e] hover:bg-[#801423] text-white text-xs font-bold rounded-xl uppercase tracking-wider transition-colors"
                >
                  Explore Full Menu
                </Link>
                <Link
                  to="/contact-us"
                  className="block w-full py-2.5 text-center bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold rounded-xl uppercase tracking-wider transition-colors"
                >
                  Contact & Directions
                </Link>
              </div>
            </div>

            {/* Why Barbequeen Widget */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                Why Diners Choose Us
              </h3>
              <ul className="space-y-3 text-xs text-gray-600">
                <li className="flex items-start gap-2.5">
                  <span className="text-[#9b1c2e] font-bold">✓</span>
                  <span>
                    <strong>Double Marinated:</strong> Signature spice blend
                    infused deep into the meat.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="text-[#9b1c2e] font-bold">✓</span>
                  <span>
                    <strong>Real Charcoal Fire:</strong> Traditional authentic
                    grilling technique.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="text-[#9b1c2e] font-bold">✓</span>
                  <span>
                    <strong>25+ Years Legacy:</strong> Serving families with
                    consistent taste since 1999.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="text-[#9b1c2e] font-bold">✓</span>
                  <span>
                    <strong>SIPCOT Location:</strong> Convenient dining and
                    takeaway hub in Pudukkottai.
                  </span>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default BestRestaurantInPudukkottaiPage;
